import { useState, useEffect } from 'react';
import styles from './ErrorDetectConfigEditor.module.scss';

function ErrorDetectConfigEditor({ configData, onConfigChange, isEditingJson }) {
  const [config, setConfig] = useState({
    enable: false,
    can_imu_missing: { 0: true, 1: false, 2: true },
    can_msg_exists: { 0: true, 1: false, 2: true },
    gnss_satellite_threshold: 5,
    gnss_position_check: true,
    rtk_check: true,
    ntrip_check: true,
    gnss_precision_threshold: 0.05,
    emcy_msg_check: { 0: true, 1: false, 2: true },
    can_error_detect: { 0: true, 1: false, 2: false },
  });

  useEffect(() => {
    if (configData && !isEditingJson) {
      try {
        const parsedConfig = JSON.parse(configData);
        setConfig(parsedConfig);
      } catch (e) {
        console.error('Error parsing config data:', e);
      }
    }
  }, [configData, isEditingJson]);

  const updateConfig = newConfig => {
    setConfig(newConfig);
    onConfigChange(JSON.stringify(newConfig, null, 2));
  };

  const handleEnableChange = e => {
    const newConfig = { ...config, enable: e.target.checked };
    updateConfig(newConfig);
  };

  const handleCanImuMissingChange = (canId, checked) => {
    const newConfig = {
      ...config,
      can_imu_missing: {
        ...(config.can_imu_missing || {}),
        [canId]: checked,
      },
    };
    updateConfig(newConfig);
  };

  const handleCanMsgExistsChange = (canId, checked) => {
    const newConfig = {
      ...config,
      can_msg_exists: {
        ...(config.can_msg_exists || {}),
        [canId]: checked,
      },
    };
    updateConfig(newConfig);
  };

  const handleGnssSatelliteThresholdChange = e => {
    const newConfig = {
      ...config,
      gnss_satellite_threshold: parseInt(e.target.value) || 0,
    };
    updateConfig(newConfig);
  };

  const handleGnssPositionCheckChange = e => {
    const newConfig = { ...config, gnss_position_check: e.target.checked };
    updateConfig(newConfig);
  };

  const handleRtkCheckChange = e => {
    const newConfig = { ...config, rtk_check: e.target.checked };
    updateConfig(newConfig);
  };

  const handleNtripCheckChange = e => {
    const newConfig = { ...config, ntrip_check: e.target.checked };
    updateConfig(newConfig);
  };

  const handleGnssPrecisionThresholdChange = e => {
    const newConfig = {
      ...config,
      gnss_precision_threshold: parseFloat(e.target.value) || 0,
    };
    updateConfig(newConfig);
  };

  const handleEmcyCheckChange = (canId, checked) => {
    const newConfig = {
      ...config,
      emcy_msg_check: {
        ...(config.emcy_msg_check || {}),
        [canId]: checked,
      },
    };
    updateConfig(newConfig);
  };

  const handleCanErrorDetectChange = (canId, checked) => {
    const newConfig = {
      ...config,
      can_error_detect: {
        ...(config.can_error_detect || {}),
        [canId]: checked,
      },
    };
    updateConfig(newConfig);
  };

  return (
    <div className={styles.container}>
      <h4 className={styles.title}>Error Detection Configuration</h4>

      {/* Enable checkbox */}
      <div className={styles.section}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={config.enable}
            onChange={handleEnableChange}
            className={styles.checkbox}
          />
          Enable Error Detection
        </label>
      </div>

      {/* CAN IMU Detect */}
      <div className={styles.section}>
        <h5 className={styles.sectionTitle}>CAN IMU Detect</h5>
        {[0, 1, 2].map(canId => (
          <div key={canId} className={styles.canRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={config.can_imu_missing?.[canId.toString()] || false}
                onChange={e => handleCanImuMissingChange(canId.toString(), e.target.checked)}
                className={styles.checkbox}
              />
              CAN{canId}
            </label>
          </div>
        ))}
      </div>

      {/* CAN Msg Check */}
      <div className={styles.section}>
        <h5 className={styles.sectionTitle}>CAN Msg Check</h5>
        {[0, 1, 2].map(canId => (
          <label key={canId} className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={config.can_msg_exists?.[canId.toString()] || false}
              onChange={e => handleCanMsgExistsChange(canId.toString(), e.target.checked)}
              className={styles.checkbox}
            />
            CAN{canId}
          </label>
        ))}
      </div>

      {/* CAN Error Detect: only CAN0 reports bus error counters for now */}
      <div className={styles.section}>
        <h5 className={styles.sectionTitle}>CAN Error Detect</h5>
        {[0, 1, 2].map(canId => {
          const supported = canId === 0;
          return (
            <label
              key={canId}
              className={`${styles.checkboxLabel} ${supported ? '' : styles.checkboxLabelDisabled}`}
            >
              <input
                type="checkbox"
                checked={supported && (config.can_error_detect?.[canId.toString()] || false)}
                disabled={!supported}
                onChange={e => handleCanErrorDetectChange(canId.toString(), e.target.checked)}
                className={styles.checkbox}
              />
              CAN{canId}
            </label>
          );
        })}
      </div>

      {/* GNSS satellite count check threshold */}
      <div className={styles.section}>
        <label className={styles.inputLabel}>
          GNSS Satellite Count Check Threshold:
          <input
            type="number"
            value={config.gnss_satellite_threshold}
            onChange={handleGnssSatelliteThresholdChange}
            className={styles.numberInput}
          />
        </label>
      </div>

      {/* GNSS Position Check */}
      <div className={styles.section}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={config.gnss_position_check}
            onChange={handleGnssPositionCheckChange}
            className={styles.checkbox}
          />
          GNSS Position Check
        </label>
      </div>

      {/* RTK Fixed Check */}
      <div className={styles.section}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={config.rtk_check}
            onChange={handleRtkCheckChange}
            className={styles.checkbox}
          />
          RTK Fixed Check
        </label>
      </div>

      {/* NTRIP Check */}
      <div className={styles.section}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={config.ntrip_check}
            onChange={handleNtripCheckChange}
            className={styles.checkbox}
          />
          NTRIP Check
        </label>
      </div>

      {/* GNSS precision threshold */}
      <div className={styles.section}>
        <label className={styles.inputLabel}>
          GNSS Precision Threshold:
          <input
            type="number"
            step="0.01"
            value={config.gnss_precision_threshold}
            onChange={handleGnssPrecisionThresholdChange}
            className={styles.numberInput}
          />
        </label>
      </div>

      {/* EMCY msg check */}
      <div className={styles.section}>
        <h5 className={styles.sectionTitle}>EMCY Msg Check</h5>
        {[0, 1, 2].map(canId => (
          <label key={canId} className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={config.emcy_msg_check?.[canId.toString()] || false}
              onChange={e => handleEmcyCheckChange(canId.toString(), e.target.checked)}
              className={styles.checkbox}
            />
            CAN{canId}
          </label>
        ))}
      </div>
    </div>
  );
}

export default ErrorDetectConfigEditor;
